import React, { useCallback, useMemo, useState } from "react";
import toast, {
  Toast,
  ToastOptions,
  resolveValue,
  useToaster,
} from "react-hot-toast";
import {
  AiFillCheckSquare,
  AiFillCloseSquare,
  AiOutlineExclamationCircle,
} from "react-icons/ai";
import { IoSyncSharp } from "react-icons/io5";

export type EwToastType = "Error" | "Success" | "Warning" | "Syncing";

export const EW_TOAST_DURATION_MS = 8000;
export const EW_TOAST_POSITION = "bottom-left" as const;
export const EW_TOAST_WIDTH_PX = 320;
/** Visible peek of each toast behind the front one (collapsed stack). */
export const EW_STACK_PEEK_PX = 10;
/** Horizontal scale reduction per depth in the stack. */
export const EW_STACK_SCALE_STEP = 0.05;
export const EW_TOAST_EXPANDED_GUTTER = 12;

export type EwToastVisual = {
  color: string;
  progressColor: string;
  icon: React.ReactNode;
};

export function getEwToastVisual(type: EwToastType): EwToastVisual {
  switch (type) {
    case "Error":
      return {
        color: "#ff8d91",
        progressColor: "#c20505",
        icon: <AiFillCloseSquare color="#ff8d91" />,
      };
    case "Syncing":
      return {
        color: "#706cff",
        progressColor: "#706cff",
        icon: <IoSyncSharp color="#706cff" />,
      };
    case "Warning":
      return {
        color: "#dc2626",
        progressColor: "#dc2626",
        icon: <AiOutlineExclamationCircle color="#dc2626" />,
      };
    default:
      return {
        color: "#61d345",
        progressColor: "#61d345",
        icon: <AiFillCheckSquare color="#61d345" />,
      };
  }
}

export type EwToastContentProps = {
  t: Toast;
  headerText: string;
  message: string | React.ReactNode;
  accentColor: string;
  progressColor: string;
  icon: React.ReactNode;
};

export function EwToastContent({
  t,
  headerText,
  message,
  accentColor,
  progressColor,
  icon,
}: EwToastContentProps) {
  const duration = t.duration ?? EW_TOAST_DURATION_MS;

  return (
    <>
      <div className="ew-hot-toast__content">
        <div className="ew-hot-toast__header">
          <div className="ew-hot-toast__header-left">
            <span className="ew-hot-toast__icon-slot">{icon}</span>
            <p className="ew-hot-toast__title">{headerText}</p>
          </div>
          <button
            type="button"
            onClick={() => toast.dismiss(t.id)}
            className="ew-hot-toast__dismiss"
            style={{ color: accentColor }}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
        <div className="ew-hot-toast__message">
          {typeof message === "string" ? <p>{message}</p> : message}
        </div>
      </div>
      <div className="ew-hot-toast__progress-track" aria-hidden>
        <div
          className="ew-hot-toast__progress-bar"
          style={{
            backgroundColor: progressColor,
            animationDuration: `${duration}ms`,
          }}
        />
      </div>
    </>
  );
}

export type EwToastNotifierVariant = "error" | "success";

export function getEwToastOptions(
  overrides?: ToastOptions & { notifierVariant?: EwToastNotifierVariant }
): ToastOptions {
  const { notifierVariant, ...rest } = overrides ?? {};
  const baseStyle: React.CSSProperties = {
    backgroundColor: "white",
    color: "rgba(0, 0, 0, 0.87)",
    fontSize: "13px",
    padding: 0,
    borderRadius: 6,
    boxShadow:
      "0 6px 22px rgba(0, 0, 0, 0.14), 0 3px 10px rgba(0, 0, 0, 0.1)",
    width: EW_TOAST_WIDTH_PX,
    maxWidth: EW_TOAST_WIDTH_PX,
    minWidth: EW_TOAST_WIDTH_PX,
    overflow: "hidden",
  };

  if (notifierVariant === "success") {
    baseStyle.backgroundColor = "#9ad4a1";
    baseStyle.fontSize = "15px";
  }

  return {
    duration: EW_TOAST_DURATION_MS,
    position: EW_TOAST_POSITION,
    className: "ew-hot-toast-item",
    style: baseStyle,
    ...rest,
  };
}

export function fireEwToast(
  type: EwToastType,
  message: string | React.ReactNode,
  heading?: string,
  options?: ToastOptions
): string | undefined {
  if (!message) return undefined;
  const { color, progressColor, icon } = getEwToastVisual(type);
  const headerText = heading ?? type;

  return toast(
    (t) => (
      <EwToastContent
        t={t}
        headerText={headerText}
        message={message}
        accentColor={color}
        progressColor={progressColor}
        icon={icon}
      />
    ),
    getEwToastOptions(options)
  );
}

function getExpandedStackBottom(
  index: number,
  toasts: Toast[],
  gutter: number
): number {
  let bottom = 0;
  const fallbackHeight = 88;
  for (let i = 0; i < index; i++) {
    bottom += (toasts[i].height ?? fallbackHeight) + gutter;
  }
  return bottom;
}

function getCollapsedStackStyle(index: number): React.CSSProperties {
  return {
    zIndex: 1000 - index,
    bottom: index * EW_STACK_PEEK_PX,
    top: "auto",
    transform: `scaleX(${Math.max(0.75, 1 - index * EW_STACK_SCALE_STEP)})`,
    transformOrigin: "bottom center",
  };
}

type EwStackedToastItemProps = {
  toast: Toast;
  index: number;
  collapsed: boolean;
  expandedBottom: number;
  onHeightUpdate: (id: string, height: number) => void;
};

function EwStackedToastItem({
  toast: t,
  index,
  collapsed,
  expandedBottom,
  onHeightUpdate,
}: EwStackedToastItemProps) {
  const measureRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node) return;
      const measure = () => {
        onHeightUpdate(t.id, node.getBoundingClientRect().height);
      };
      measure();
      const observer = new MutationObserver(measure);
      observer.observe(node, {
        subtree: true,
        childList: true,
        characterData: true,
      });
    },
    [t.id, onHeightUpdate]
  );

  const stackStyle: React.CSSProperties = collapsed
    ? getCollapsedStackStyle(index)
    : {
        zIndex: 1000 - index,
        bottom: expandedBottom,
        top: "auto",
        transform: "none",
      };

  const boxStyle: React.CSSProperties = {
    ...getEwToastOptions().style,
    ...t.style,
  };

  return (
    <li
      className="ew-hot-toast-stack-item"
      style={stackStyle}
      data-stack-index={index}
    >
      <div
        ref={measureRef}
        className={t.className || "ew-hot-toast-item"}
        style={boxStyle}
      >
        {resolveValue(t.message, t)}
      </div>
    </li>
  );
}

export function EwToaster() {
  const [stackCollapsed, setStackCollapsed] = useState(true);
  const { toasts, handlers } = useToaster(getEwToastOptions());

  const visibleToasts = useMemo(
    () =>
      toasts.filter(
        (t) =>
          t.visible &&
          (t.position || EW_TOAST_POSITION) === EW_TOAST_POSITION
      ),
    [toasts]
  );

  const stackHeight = useMemo(() => {
    if (!visibleToasts.length) return 0;
    const fallbackHeight = 88;
    if (stackCollapsed) {
      const front = visibleToasts[0].height ?? fallbackHeight;
      const peek = Math.max(0, visibleToasts.length - 1) * EW_STACK_PEEK_PX;
      return front + peek;
    }
    return visibleToasts.reduce((sum, t, i) => {
      const h = t.height ?? fallbackHeight;
      return sum + h + (i > 0 ? EW_TOAST_EXPANDED_GUTTER : 0);
    }, 0);
  }, [visibleToasts, stackCollapsed]);

  const handleMouseEnter = () => {
    handlers.startPause();
    setStackCollapsed(false);
  };

  const handleMouseLeave = () => {
    handlers.endPause();
    setStackCollapsed(true);
  };

  if (!visibleToasts.length) return null;

  return (
    <div
      id="_rht_toaster"
      className="ew-hot-toast-container"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <ul
        className={`ew-hot-toast-stack${
          stackCollapsed ? "" : " ew-hot-toast-stack--expanded"
        }`}
        style={{ height: stackHeight }}
      >
        {visibleToasts.map((t, index) => (
          <EwStackedToastItem
            key={t.id}
            toast={t}
            index={index}
            collapsed={stackCollapsed}
            expandedBottom={getExpandedStackBottom(
              index,
              visibleToasts,
              EW_TOAST_EXPANDED_GUTTER
            )}
            onHeightUpdate={handlers.updateHeight}
          />
        ))}
      </ul>
    </div>
  );
}
